import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { GenericFormlyFormComponent } from '@intalio/formly-builder/bootstrap';
import { ServicesFormService } from '../../../services/services-form.service';
import { BACKEND_API_BASE_URL } from '../../../config/app-settings.tokens';

/**
 * Task Viewer - Tab 2: Task Form
 * Displays editable form for task processing with action buttons
 * Supports conditional readonly mode based on pageType
 */
@Component({
  selector: 'app-task-form-tab',
  standalone: true,
  templateUrl: './task-form-tab.component.html',
  styleUrls: ['./task-form-tab.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GenericFormlyFormComponent,
  ]
})
export class TaskFormTabComponent implements OnInit, OnDestroy {
  @ViewChild(GenericFormlyFormComponent) genericForm?: GenericFormlyFormComponent;

  @Input() formSchema: string = '';
  @Input() formData: string = '';
  @Input() availableActions: string[] = [];
  @Input() taskRequest: any = null;
  @Input() requestId: string | null = null;
  @Input() taskId: string | null = null;
  @Input() requestTypeLogicalId: string = '';
  @Input() isReadonly: boolean = false;
  @Input() pageType: 'inbox' | 'sent' | 'completed' | 'myRequests' = 'inbox';

  @Output() actionExecuted = new EventEmitter<{ action: string; formData: any }>();
  @Output() formDataChanged = new EventEmitter<any>();

  /** Raw form definition JSON passed to the library */
  formDefinition: any = null;
  model: any = {};
  isLoading: boolean = false;
  errorMessages: string[] = [];
  successMessage: string = '';

  private destroy$ = new Subject<void>();
  private formService = inject(ServicesFormService);
  private router = inject(Router);
  private http = inject(HttpClient);
  private readonly backendApiBaseUrl = inject(BACKEND_API_BASE_URL);

  ngOnInit(): void {
    if (this.pageType !== 'inbox') {
      this.loadFormFromApi();
    } else {
      this.parseAndSetFields();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get shouldDisableInputs(): boolean {
    return this.availableActions.length === 0;
  }

  private loadFormFromApi(): void {
    if (!this.requestId || !this.taskId) {
      this.errorMessages = ['Missing requestId or taskId'];
      return;
    }

    this.isLoading = true;
    this.errorMessages = [];

    const apiUrl = `${this.backendApiBaseUrl}/request/id/${this.requestId}/task/${this.taskId}/form`;
    this.http.get<any>(apiUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const formDesigner = response.formDesigner || {};
          this.formDefinition = formDesigner.formJson || [];
          this.model = response.formData || {};
          this.isReadonly = true;
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error('[TaskFormTab] Error loading form from API:', error);
          this.errorMessages = ['Failed to load form details'];
          this.isLoading = false;
        }
      });
  }

  private parseAndSetFields(): void {
    if (!this.formSchema) {
      return;
    }

    try {
      let parsed = JSON.parse(this.formSchema);

      // Handle FormIO schema wrapper
      if (parsed.components && Array.isArray(parsed.components) && parsed.components.length > 0) {
        const firstComponent = parsed.components[0];
        if (firstComponent.defaultValue && typeof firstComponent.defaultValue === 'string') {
          try {
            parsed = JSON.parse(firstComponent.defaultValue);
          } catch {
            parsed = parsed.components;
          }
        }
      }

      // Extract fields array
      let rawFields: any[];
      if (Array.isArray(parsed)) {
        rawFields = parsed;
      } else if (parsed.components && Array.isArray(parsed.components)) {
        rawFields = parsed.components;
      } else {
        this.formDefinition = null;
        return;
      }

      this.formDefinition = rawFields;

      // Populate model from formData input
      if (this.formData) {
        try {
          this.model = JSON.parse(this.formData);
        } catch {
          this.model = {};
        }
      }
    } catch (error) {
      console.error('[TaskFormTab] Error parsing schema:', error);
      this.errorMessages = ['Failed to load form schema'];
      this.formDefinition = null;
    }
  }

  onFormSubmit(event: {
    action: string;
    formData: any;
    files: File[];
    fileMetadata: any[];
    requestId: number | null;
    originalModel: any;
  }): void {
    if (!this.requestId || !this.taskId || !this.requestTypeLogicalId) {
      this.errorMessages = ['Missing required parameters (requestId, taskId, or requestTypeLogicalId)'];
      return;
    }

    // The library emits `formData` as a plain (cleaned) object plus separate `files`/`fileMetadata`.
    // Use the library's helper to build a real Web FormData with the right key shape.
    const formData = this.genericForm?.getFormDataForSubmission();
    if (!formData) {
      this.errorMessages = ['Failed to build form data for submission'];
      return;
    }

    this.isLoading = true;
    this.errorMessages = [];
    this.successMessage = '';

    // The backend's execute-action endpoint does `int.Parse` on the
    // requestTypeLogicalId field, so it must be a numeric string. Some response
    // shapes give us the enum NAME (e.g. "FireSafetyPrevention") in
    // details.requestTypeLogicalId; the numeric value lives on details.requestTypeId.
    // Prefer the numeric form when available, fall back to the logical id otherwise.
    const numericRequestType =
      this.taskRequest?.details?.requestTypeId != null
        ? String(this.taskRequest.details.requestTypeId)
        : this.requestTypeLogicalId;

    this.formService
      .applyAction(
        this.requestId,
        event.action,
        this.taskId,
        numericRequestType,
        formData
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // isLoading deliberately stays true: the redirect is 1.5s away and the
          // library's action buttons would otherwise be clickable again in the
          // meantime, letting the user re-run an action the workflow has already
          // advanced past. The overlay comes down when the route destroys us.
          this.successMessage = `Action "${event.action}" completed successfully`;
          this.actionExecuted.emit({ action: event.action, formData: this.model });
          setTimeout(() => this.router.navigate(['/requests/inbox']), 1500);
        },
        error: (error: any) => {
          this.isLoading = false;
          this.errorMessages = [this.extractErrorMessage(error)];
          console.error('[TaskFormTab] Action error:', error);
        }
      });
  }

  private extractErrorMessage(error: any): string {
    if (error.status === 400 && error.error) {
      if (Array.isArray(error.error)) {
        return error.error.map((e: any) => e.message || e).join(', ');
      }
      if (error.error.errors && Array.isArray(error.error.errors)) {
        return error.error.errors.map((e: any) => e.message || e).join(', ');
      }
      if (error.error.message) return error.error.message;
    }
    if (error.status === 500) return error.error?.message || 'Server error occurred';
    if (error.message) return error.message;
    return 'Failed to execute action';
  }

}
